import React, { useContext } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router';
import styled, { ThemeContext } from 'styled-components';
import RiskObjectIcon from '../../../../common/src/components/RiskObjectIcon';
import ToolbarOfferNavItemMobile from '../default/ToolbarOfferNavItemMobile';
import { IconArrow, IconChevronLeftRounded } from 'wg-fe-ui';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../store/redux/reducers/rootReducer';
import { HStack } from '@wegroup/design-system';
import { getOfferRiskStatus } from '../../services/offersService';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import { useFlowsSelector } from '../../../../common/src/utils/globalVariables';

interface Params {
  sessionId?: string;
  page?: string;
  riskObject?: string;
}

const OffersToolbarContainerMobile: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const theme = useContext(ThemeContext);
  const sessionId = useSessionId();
  const flowId = useFlowsSelector((state) => state.flows.metaData.flowId);

  const { page, riskObject }: Params =
    matchPath(location.pathname, {
      path: `/product/session/offers/:page/:riskObject?`,
      exact: true,
    })?.params || {};
  const [selectedRiskObjects, selectedOffers, visitedOverviewPages]: [
    string[],
    { [x: string]: string[] },
    string[],
  ] = useSelector(
    (state: RootState) => [
      state.selection.selectedRiskObjects,
      state.offers.selectedOffers,
      state.offers.visitedOverviewPages,
    ],
    shallowEqual,
  );
  const handleNav = (navId: string) => {
    if (navId === 'selectedOffers') {
      history.push(
        `/product/session/offers/selectedOffers?session_id=${sessionId}`,
      );
    } else
      history.push(
        `/product/session/offers/overview/${navId}?session_id=${sessionId}`,
      );
  };

  return (
    <Wrapper>
      {page === 'selectedOffers' ? (
        <CartView>
          <div onClick={() => history.goBack()}>
            <StyledIconArrow color={theme.primary[500]} />
          </div>
          <Title>{t('Your selected offers')}</Title>
        </CartView>
      ) : (
        <Container>
          <BackButton
            data-test-id="ProdFac_QuestionViewHeader_Back"
            onClick={() =>
              history.push(`/flows/${flowId}/questions/${sessionId}`)
            }
          >
            <IconChevronLeftRounded color={theme.primary[500]} size="20" />
          </BackButton>
          <HStack justifyContent="center" w="100%">
            {[...selectedRiskObjects].sort().map((riskKey: string) => (
              <ToolbarOfferNavItemMobile
                key={`toolbar_item_${riskKey}`}
                Icon={
                  <RiskObjectIcon
                    riskObject={riskKey}
                    isImmo={theme['isImmo']}
                  />
                }
                active={riskObject === riskKey}
                onClick={() => handleNav(riskKey)}
                status={getOfferRiskStatus(
                  riskKey,
                  selectedOffers,
                  visitedOverviewPages,
                )}
              >
                {t(riskKey, { defaultValue: t(riskKey.toUpperCase()) })}
              </ToolbarOfferNavItemMobile>
            ))}
          </HStack>
        </Container>
      )}
    </Wrapper>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100vw;
  margin: 0 0.8rem;
`;

const BackButton = styled.button`
  background: none;
  outline: none;
  padding: 0;
  border: none;

  > svg {
    height: 4rem;
    width: 4rem;
  }
`;

const Title = styled.p`
  text-align: center;
  font-size: 1.6rem;
  line-height: 120%;
  color: ${({ theme }) => theme.primary[500]};
`;

const StyledIconArrow = styled(IconArrow)`
  width: 2.4rem;
  height: 2.4rem;
  position: absolute;
  left: 2.4rem;
  top: 50%;
  transform: rotate(180deg) translateY(50%);
  z-index: 1;
  display: block;
`;

const CartView = styled.div`
  width: 100%;
  position: relative;
  padding: 0 3.2rem;
`;

const Wrapper = styled.div`
  min-height: 8rem;
  display: flex;
  align-items: center;
  margin: 0 auto;
  width: 100vw;
`;

export default OffersToolbarContainerMobile;
